package er.prateek.crossly.ui.streak

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.achievement_awesome
import crossly.shared.generated.resources.restore_ad_failed
import crossly.shared.generated.resources.restore_body_many
import crossly.shared.generated.resources.restore_body_one
import crossly.shared.generated.resources.restore_expires
import crossly.shared.generated.resources.restore_free
import crossly.shared.generated.resources.restore_kicker
import crossly.shared.generated.resources.restore_not_now
import crossly.shared.generated.resources.restore_progress
import crossly.shared.generated.resources.restore_stars_balance
import crossly.shared.generated.resources.restore_success_body
import crossly.shared.generated.resources.restore_success_title
import crossly.shared.generated.resources.restore_title
import crossly.shared.generated.resources.restore_unavailable
import crossly.shared.generated.resources.restore_use_stars
import crossly.shared.generated.resources.restore_watch_ad
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.ui.achievements.HoldCelebrations
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.IconFlame
import er.prateek.crossly.ui.components.IconPlay
import er.prateek.crossly.ui.components.IconStar
import er.prateek.crossly.ui.components.SparkBurst
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlin.time.Clock
import kotlin.time.Duration
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

/**
 * Buying back a frozen streak. The price is set by [er.prateek.crossly.progression.StreakManager]
 * (one rewarded video inside the first day, two after), progress survives closing the sheet, and
 * every player has a path: ad-free players restore free, and players who can't be shown a video
 * (desktop/web, no fill) can spend stars instead.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StreakRestoreSheet(
    source: String,
    onClose: () -> Unit,
) {
    val colors = MaterialTheme.game
    val streakManager = AppContainer.streakManager
    val ads = AppContainer.adsCoordinator
    val scope = rememberCoroutineScope()
    val entitlement by ads.entitlement.collectAsState()

    var restoredStreak by remember { mutableStateOf<Int?>(null) }
    var busy by remember { mutableStateOf(false) }
    var adFailed by remember { mutableStateOf(false) }
    // Ticks the countdown; the offer itself is re-read from the manager on every recomposition.
    var now by remember { mutableStateOf(Clock.System.now()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(30_000)
            now = Clock.System.now()
        }
    }
    val offer = streakManager.restoreOffer()
    // The restore is its own celebration; any badge it unlocks (Phoenix) plays after the sheet closes.
    HoldCelebrations()

    LaunchedEffect(Unit) {
        val shown = streakManager.restoreOffer() ?: return@LaunchedEffect
        AppContainer.analytics.logEvent(
            AnalyticsEvents.STREAK_RESTORE_OFFER_SHOWN,
            mapOf(
                "source" to source,
                "lost_streak" to shown.lostStreak,
                "ads_required" to shown.adsRequired,
                "ads_watched" to shown.adsWatched,
                "within_grace" to shown.withinGrace,
            ),
        )
    }
    // The window closed (or the offer was used elsewhere) while open and nothing was restored.
    LaunchedEffect(offer == null, restoredStreak) {
        if (offer == null && restoredStreak == null) onClose()
    }

    fun finish(method: String, lost: Int) {
        restoredStreak = lost
        AppContainer.playerJourney.onStreakRestored(method, lost)
    }

    ModalBottomSheet(
        onDismissRequest = {
            if (restoredStreak == null) {
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.STREAK_RESTORE_DISMISSED,
                    mapOf("source" to source, "ads_watched" to (offer?.adsWatched ?: 0)),
                )
            }
            onClose()
        },
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
        containerColor = colors.card,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Spacing.xl, vertical = Spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            val restored = restoredStreak
            if (restored != null) {
                RestoredIllustration()
                Text(
                    text = stringResource(Res.string.restore_success_title),
                    style = MaterialTheme.typography.headlineSmall,
                    color = colors.ink,
                    textAlign = TextAlign.Center,
                )
                Text(
                    text = stringResource(Res.string.restore_success_body),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.inkMuted,
                    textAlign = TextAlign.Center,
                )
                GameButton(
                    text = stringResource(Res.string.achievement_awesome),
                    onClick = onClose,
                    style = GameButtonStyle.GOLD,
                    fillWidth = true,
                    modifier = Modifier.padding(top = Spacing.sm),
                )
                return@Column
            }
            if (offer == null) return@Column

            FrozenFlame()
            Text(
                text = stringResource(Res.string.restore_kicker),
                style = MaterialTheme.typography.labelLarge.copy(letterSpacing = 2.sp, fontWeight = FontWeight.Black),
                color = iceColor(),
            )
            Text(
                text = stringResource(Res.string.restore_title, offer.lostStreak),
                style = MaterialTheme.typography.headlineSmall,
                color = colors.ink,
                textAlign = TextAlign.Center,
            )
            Text(
                text = if (offer.adsRequired <= 1) {
                    stringResource(Res.string.restore_body_one)
                } else {
                    stringResource(Res.string.restore_body_many, offer.adsRequired)
                },
                style = MaterialTheme.typography.bodyMedium,
                color = colors.inkMuted,
                textAlign = TextAlign.Center,
            )
            Text(
                text = stringResource(Res.string.restore_expires, formatRemaining(offer.expiresAt - now)),
                style = MaterialTheme.typography.labelLarge,
                color = colors.flame,
            )

            if (offer.adsRequired > 1) {
                AdProgress(watched = offer.adsWatched, required = offer.adsRequired)
            }

            val adFree = entitlement.adFree
            val adAvailable = !adFree && ads.isRewardedAvailable()
            val starCost = offer.starCostRemaining
            val starsAvailable = AppContainer.progressionManager.starsAvailable()
            val canPayStars = !adFree && offer.starCostPerAd > 0 && starsAvailable >= starCost

            if (adFailed) {
                Text(
                    text = stringResource(Res.string.restore_ad_failed),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.danger,
                    textAlign = TextAlign.Center,
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth().padding(top = Spacing.sm),
                verticalArrangement = Arrangement.spacedBy(Spacing.sm),
            ) {
                when {
                    adFree -> GameButton(
                        text = stringResource(Res.string.restore_free),
                        onClick = {
                            val lost = offer.lostStreak
                            if (streakManager.restore()) finish("ad_free", lost)
                        },
                        style = GameButtonStyle.GOLD,
                        fillWidth = true,
                    )
                    adAvailable -> GameButton(
                        text = stringResource(Res.string.restore_watch_ad) +
                            if (offer.adsRequired > 1) "  ${offer.adsWatched + 1}/${offer.adsRequired}" else "",
                        onClick = {
                            if (busy) return@GameButton
                            busy = true
                            adFailed = false
                            scope.launch {
                                AppContainer.analytics.logEvent(
                                    AnalyticsEvents.AD_REWARDED_REQUESTED,
                                    mapOf("placement" to AdPlacement.STREAK_RESTORE.wireName),
                                )
                                val lost = offer.lostStreak
                                val earned = ads.showRewarded(AdPlacement.STREAK_RESTORE)
                                if (earned) {
                                    AppContainer.analytics.logEvent(
                                        AnalyticsEvents.STREAK_RESTORE_AD_EARNED,
                                        mapOf("ad_index" to offer.adsWatched + 1, "ads_required" to offer.adsRequired),
                                    )
                                    if (streakManager.recordRestoreAd()) finish("rewarded_ad", lost)
                                } else {
                                    adFailed = true
                                }
                                busy = false
                            }
                        },
                        enabled = !busy,
                        style = GameButtonStyle.GOLD,
                        fillWidth = true,
                        icon = { tint -> IconPlay(size = 18.dp, tint = tint) },
                    )
                    !canPayStars -> Text(
                        text = stringResource(Res.string.restore_unavailable),
                        style = MaterialTheme.typography.bodySmall,
                        color = colors.inkMuted,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                if (canPayStars) {
                    GameButton(
                        text = stringResource(Res.string.restore_use_stars, starCost),
                        onClick = {
                            val lost = offer.lostStreak
                            if (AppContainer.progressionManager.spendStar(starCost) && streakManager.restore()) {
                                finish("stars", lost)
                            }
                        },
                        style = if (adAvailable) GameButtonStyle.SECONDARY else GameButtonStyle.GOLD,
                        fillWidth = true,
                        icon = { tint -> IconStar(size = 18.dp, tint = tint) },
                        caption = stringResource(Res.string.restore_stars_balance, starsAvailable),
                    )
                }
                GameButton(
                    text = stringResource(Res.string.restore_not_now),
                    onClick = {
                        AppContainer.analytics.logEvent(
                            AnalyticsEvents.STREAK_RESTORE_DISMISSED,
                            mapOf("source" to source, "ads_watched" to offer.adsWatched),
                        )
                        onClose()
                    },
                    style = GameButtonStyle.GHOST,
                    fillWidth = true,
                )
            }
        }
    }
}

@Composable
private fun AdProgress(watched: Int, required: Int) {
    val colors = MaterialTheme.game
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(Spacing.xs)) {
        Row(horizontalArrangement = Arrangement.spacedBy(Spacing.sm)) {
            repeat(required) { i ->
                Box(
                    modifier = Modifier
                        .width(48.dp)
                        .height(8.dp)
                        .background(
                            if (i < watched) colors.gold else colors.locked.copy(alpha = 0.3f),
                            RoundedCornerShape(Radius.chip),
                        ),
                )
            }
        }
        Text(
            text = stringResource(Res.string.restore_progress, (watched + 1).coerceAtMost(required), required),
            style = MaterialTheme.typography.labelSmall,
            color = colors.inkMuted,
        )
    }
}

/** An ice-ringed flame that "breathes" -- frozen, but still worth saving. */
@Composable
private fun FrozenFlame() {
    val ice = iceColor()
    val transition = rememberInfiniteTransition(label = "frozenFlame")
    val shimmer by transition.animateFloat(
        initialValue = 0.35f,
        targetValue = 0.8f,
        animationSpec = infiniteRepeatable(tween(1_100), RepeatMode.Reverse),
        label = "iceShimmer",
    )
    Box(
        modifier = Modifier
            .size(104.dp)
            .background(ice.copy(alpha = 0.16f), CircleShape)
            .border(3.dp, ice.copy(alpha = shimmer), CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        IconFlame(size = 56.dp, tint = ice)
    }
}

@Composable
private fun RestoredIllustration() {
    val colors = MaterialTheme.game
    val transition = rememberInfiniteTransition(label = "restoredFlame")
    val pulse by transition.animateFloat(
        initialValue = 1f,
        targetValue = 1.12f,
        animationSpec = infiniteRepeatable(tween(700), RepeatMode.Reverse),
        label = "restoredPulse",
    )
    Box(modifier = Modifier.size(180.dp), contentAlignment = Alignment.Center) {
        SparkBurst(seed = 42, pieceCount = 60)
        Box(
            modifier = Modifier
                .size(104.dp)
                .graphicsLayer { scaleX = pulse; scaleY = pulse }
                .background(colors.goldBrush, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            IconFlame(size = 58.dp, tint = colors.onAccent, coreTint = colors.gold)
        }
    }
}

private fun formatRemaining(remaining: Duration): String {
    val totalMinutes = remaining.inWholeMinutes.coerceAtLeast(0)
    val days = totalMinutes / (24 * 60)
    val hours = (totalMinutes / 60) % 24
    val minutes = totalMinutes % 60
    return when {
        days > 0 -> "${days}d ${hours}h"
        hours > 0 -> "${hours}h ${minutes}m"
        else -> "${minutes}m"
    }
}
